import type { IPrivateApiFields } from './CommonTypes';
import type { DriverBreakRule } from './DriverBreak';
import type { ClientIntermodalPreferences } from './Client';

/**
 * String enum of possible client types.
 */
enum ClientType {
    STANDARD = 'standard',
    DRIVER_GROUP = 'driver-group'
}

export enum ZoneBasedRoutingOption {
    STRICT = 'strict',
    FLEXIBLE = 'flexible',
    DISABLED = 'disabled'
}

/**
 * String enum of possible pick up delivery models
 */
export enum PickupDeliveryModelType {
    TAXICAB = 'taxicab',
    CARPOOLING = 'carpooling'
}

export interface DriverRecommendation {
    enabled: boolean;
    topTakenPercentage: number;
    skipConstraintChecking: boolean;
}

export interface ClientPreferences {
    maxRouteDuration: number;
    maxTripDuration: number;
    enableCapacityConstraints: boolean;
    lockClusterInitialSolution: boolean;
    balanceBasedOnDuration: boolean;
    balanceBasedOnStops: boolean;
    balanceBasedOnMileage: boolean;
    balanceBasedOnVolume: boolean;
    enableGoogleApiEta: boolean;
    enableGoogleDirectionsApi: boolean;
    shippingUnits: string;
    enableRouteBuilder: boolean;
    enableScheduler: boolean;
    costPerUnitDistance: number;
    machineLearning: number;
    mlSequencing: boolean;
    acceptDelays: boolean;
    acceptEarly: boolean;
    enableDriverTracks: boolean;
    enableClusterInitialSolution: boolean;
    enableDriverShiftTime: boolean;
    useOriginalStopOrder: boolean;
    maxTaskOnRoute: number;
    enableRtaOnEdit: boolean;
    enableTrafficTimes: boolean;
    enableMultiTripOptimization: boolean;
    enableTruckRoutes: boolean;
    createExtraVehicles: boolean;
    enableDriverSequenceML: boolean;
    enableDriverSequenceMLGeo: boolean;
    avoidDifficultTurns: boolean;
    avoidTolls: boolean;
    enableHighPrecisionETA: boolean;
    enableLocationGrid: boolean;
    enableGooglePlacesGeocoding: boolean;
    allowApproximateGeocoding: boolean;
    enableContainerizedScheduler: boolean;
    enableServerlessRouteEditing: boolean;
    forceLongRunningPlannerInfra: boolean;
    usePreCluster: boolean;
    enableTaskConsolidation: boolean;
    enableScheduledNotifications: boolean;
    pickupDeliveryModel: PickupDeliveryModelType;
    useDeliveryTWForTwoPart: boolean;
    useAllAvailableDrivers: boolean;
    limitReorder: number;
    zoneBasedRouting: ZoneBasedRoutingOption;
    sendRtaNotification: boolean;
    disableInProgressAssignmentStatus: boolean;
    enableTwoPartTaskEditing: boolean;
    maxRouteLengthMeter: number;
    balanceBasedOnItems: boolean;
    enableDriverBooking: boolean;
    addDepotsOnDemandToInactiveDriver: boolean;
    enableDriverManagement: boolean;
    enableParallelScheduler: boolean;
    improveRoutesAttractiveness: boolean;
    startRouteImmediately: boolean;
    penalizeWaitTimeProportionally: boolean;
    penalizeLastEdge: boolean;
    minimizeRouteCrossingsInRTA: boolean;
    minimizeWaitingTimes: boolean;
    useCustomCost: boolean;
    autoArriveMode: string;
    defaultGeofenceRadius: number;
    minNumRoutes: number;
    allowDispatchingFutureDates: boolean;
    startAllTripsFromStartDepot: boolean;
    useClientCountry: boolean;
    enableContinuousDispatching: boolean;
    enableCalculateImpact: boolean;
    allowEditingAssignedTasks: boolean;
    useHereMapTiles: boolean;
    driverRecommendation: DriverRecommendation;
    showDispatchCancelButton: boolean;
    driverPrivacy: boolean;
    snapToRoad: boolean;
    customLogo: string;
    /**
     * Whether the duplicate tasks feature is enabled
     */
    enableDuplicateTask: boolean;
    /**
     * Whether the driver assistants feature is enabled
     */
    enableDriverAssistants: boolean;
    /**
     * The custom knowledge base url
     *
     * When set, will have an absolute URL string value.
     * Otherwise, it will be set with the value `default`
     */
    knowledgeBaseUrl: string;
}

/**
 * Client Preferences related to UI
 * uiPreferences are defined at the top level of client object i.e. {preferences: {...}, {uiPreferences: {...}}}
 */
export interface ClientUIPreferences {
    enableUserManagement: boolean;
    enableDoorDashIntegration: boolean;
    enableAssignmentTypeDisplay: boolean;
    completedRoutesRequireEndDepot: boolean;
    showLicensePlateOnRoute: boolean;
    use24HourFormat: boolean;
}

export interface ClientTemplate {
    integrationDownload?: string[];
    integrationExport?: string[];
    integrationName?: string;
}

export interface ClientCourierPreferences {
    maxCompensationPercentage: number;
    minRevenuePerRoute: number;
    capacityBuffer: number;
    revenuePerItem: number;
    servicedLocations: string[];
}

export interface ClientNotifications {
    enableCustomerPortal: boolean;
    displayCustomPortal: boolean;
    customPortalLogoUrl: string;
    emailNotification: boolean;
    smsNotification: boolean;
    notifyOnCreateAssignment: boolean;
    notifyOnStartAssignment: boolean;
    notifyOnCompleteAssignment: boolean;
    notifyBasedOnTime: boolean;
    notificationTime: number;
    notifyOnRequest: boolean;
    notifyOnDelayChange: boolean;
    notifyOnScannedDelivered: boolean;
    notifyOnScannedLoading: boolean;
    shareLocationNStopsAway: number;
}

export interface ClientOnboardingCustomerInformation {
    businessDriverBookingPotential: string[];
    businessIndustry: string;
    businessWarehouseCount: string;
    businessShippingAccounts: string;
    businessStopsPerDay: string;
    businessEnableSameDayWaveScheduling: true;
    businessRouteOptimization: string;
    businessTaskType: string;
}

export interface ClientOnboarding {
    customerInformation: ClientOnboardingCustomerInformation;
    onboarded: true;
}

export type ClientPreferenceKeys = keyof ClientPreferences;

/**
 * Type for API client
 */
export interface ApiClient extends IPrivateApiFields {
    /**
     * the client's id
     */
    id: string;
    /**
     * the client's name
     */
    name: string;
    /**
     * the client's address id
     */
    address: string;
    /**
     * the client's domain
     */
    domain: string;
    /**
     * the client's preferences
     */
    preferences: ClientPreferences;
    /**
     * the client's UI preferences
     */
    uiPreferences: ClientUIPreferences;
    /**
     * the client's driver break rules
     */
    driverBreakRules: DriverBreakRule[];
    /**
     * the client's courier preferences
     */
    courierPreferences: ClientCourierPreferences;
    /**
     * the client's notification preferences
     */
    notifications: ClientNotifications;
    /**
     * the client's onboarding information
     */
    onboarding: ClientOnboarding;
    /**
     * the client's intermodal preferences
     */
    intermodal?: ClientIntermodalPreferences;
    /**
     * the client's code
     */
    code: string;
    /**
     * the client's timezone (TZ database name, like America/New_York)
     */
    timezone: string;
    /**
     * indicates if a client is a driver_group or a standard client
     */
    type: ClientType;
    /**
     * client's related driver groups
     */
    driver_groups: string[];
}
